/**
 * Estimate computation.
 *
 * Inputs: pricing_rules from /api/pricing-rules + the draft payload.
 * Output: { min, max, estimate } in INR, or null when not enough data.
 *
 * Algorithm (per spec):
 *   base × guest × duration × locations × drone × video × peak × destination
 *   range = [estimate × (1 - spread/2), estimate × (1 + spread/2)]
 */

const MIN_FIELDS_FOR_ESTIMATE = ["occasion"];

export function hasEnoughForEstimate(payload) {
  if (!payload) return false;
  return MIN_FIELDS_FOR_ESTIMATE.every((k) => Boolean(payload[k]));
}

export function computeEstimate(payload, pricingRules) {
  if (!payload || !pricingRules) return null;
  if (!hasEnoughForEstimate(payload)) return null;

  const occ = payload.occasion;
  const baseMap = pricingRules.base_by_occasion || {};
  const base = baseMap[occ] != null ? baseMap[occ] : baseMap.other;
  if (!base || !Number.isFinite(base)) return null;

  let mult = 1;

  // Guest count band (from Step 3 questions)
  const guestBand = payload?.scope?.guest_count_band;
  const guestMap = pricingRules.guest_count_multiplier || {};
  if (guestBand && guestMap[guestBand]) {
    mult *= guestMap[guestBand];
  }

  // Duration band
  const duration = payload?.scope?.duration;
  const durMap = pricingRules.duration_multiplier || {};
  if (duration && durMap[duration]) {
    mult *= durMap[duration];
  }

  // Locations beyond first
  const locs = Array.isArray(payload?.locations) ? payload.locations.length : 0;
  const perExtra = pricingRules.location_count_multiplier?.per_extra || 0;
  if (locs > 1) {
    mult *= 1 + (locs - 1) * perExtra;
  }

  // Drone
  if (payload?.scope?.drone === "yes" && pricingRules.drone_multiplier) {
    mult *= pricingRules.drone_multiplier;
  }

  // Video deliverables
  const video = payload?.scope?.video_deliverables;
  const videoMap = pricingRules.video_multiplier || {};
  if (video && videoMap[video]) {
    mult *= videoMap[video];
  }

  // Peak — true if any exact_date is marked 'peak'
  if (payload?.peak_flag === true && pricingRules.peak_multiplier) {
    mult *= pricingRules.peak_multiplier;
  }

  // Destination
  if (
    payload?.location_mode === "destination" &&
    pricingRules.destination_multiplier
  ) {
    mult *= pricingRules.destination_multiplier;
  }

  const estimate = base * mult;
  const spread = pricingRules.range_spread || 0.35;
  const min = Math.round(estimate * (1 - spread / 2));
  const max = Math.round(estimate * (1 + spread / 2));

  return { min, max, estimate: Math.round(estimate) };
}
